/**
 * Per-user preview preferences kept under `settings.ext` (keys `preview.*`; they appear on the Settings page through
 * the module's SettingDefs and reset like every setting): screen shape, readout, gizmo snapping, test play mode and
 * lead-in. Everything else of the preview lives in the editor store (`preview`) and in `settings.preview`.
 */

import type { SettingsService } from '../../app/settings/types';
import type { Json } from '../../core/util/types';

export const EXT_SCREEN = 'preview.screen';
export const EXT_READOUT = 'preview.readout';
export const EXT_SNAP = 'preview.snap';
export const EXT_TEST_MODE = 'preview.testPlayMode';
export const EXT_LEAD_IN = 'preview.testPlayLeadIn';
/** Lane <-> preview hover linking (default on). */
export const EXT_LANE_LINK = 'preview.laneLink';
/** Preview-only background dim override (Phira settings popover; null = the chart's info.yml value). */
export const EXT_BG_DIM = 'preview.backgroundDim';

/**
 * Simulated device screen: 'chart' = the chart's aspect ratio (info.yml aspectRatio, the default), 'fill' = the
 * panel itself (Phira letterboxes inside it), or a device aspect ("as seen on 4:3 / 3:2 / 19.5:9 / 21:9").
 */
export type ScreenShape = 'chart' | 'fill' | '16:9' | '4:3' | '3:2' | '19.5:9' | '21:9';

export const SCREEN_SHAPES: readonly ScreenShape[] = ['chart', 'fill', '16:9', '4:3', '3:2', '19.5:9', '21:9'];

const SHAPE_ASPECT: Readonly<Record<Exclude<ScreenShape, 'chart' | 'fill'>, number>> = {
  '16:9': 16 / 9,
  '4:3': 4 / 3,
  '3:2': 3 / 2,
  '19.5:9': 19.5 / 9,
  '21:9': 21 / 9,
};

export function isScreenShape(v: unknown): v is ScreenShape {
  return typeof v === 'string' && (SCREEN_SHAPES as readonly string[]).includes(v);
}

export function screenShapeOf(json: Json | undefined): ScreenShape {
  return isScreenShape(json) ? json : 'chart';
}

/**
 * Aspect of the simulated screen (null = fill the host). In Theater and test play the "chart" shape fills the
 * window (Phira pillarboxes inside it, like on a monitor); a device shape keeps its aspect everywhere.
 */
export function screenAspect(shape: ScreenShape, chartAspect: number, fullWindow: boolean): number | null {
  if (shape === 'fill') return null;
  if (shape === 'chart') return fullWindow ? null : chartAspect;
  return SHAPE_ASPECT[shape];
}

/** Safe-area guide aspects (DESIGN.md §4.8). */
export type SafeArea = 'none' | '16:9' | '4:3' | '3:2';
export const SAFE_AREAS: readonly SafeArea[] = ['none', '16:9', '4:3', '3:2'];

export function safeAreaAspect(a: SafeArea): number | null {
  switch (a) {
    case '16:9':
      return 16 / 9;
    case '4:3':
      return 4 / 3;
    case '3:2':
      return 3 / 2;
    default:
      return null;
  }
}

/** Gizmo snapping (toolbar): canvas grid, screen centre and edges, other lines' anchors. */
export interface SnapPrefs {
  readonly grid: boolean;
  /** Grid step in RPE px. */
  readonly gridSize: number;
  readonly centre: boolean;
  readonly edges: boolean;
  readonly anchors: boolean;
}

export const DEFAULT_SNAP: SnapPrefs = Object.freeze({
  grid: true,
  gridSize: 25,
  centre: true,
  edges: true,
  anchors: true,
});

export function snapPrefsOf(json: Json | undefined): SnapPrefs {
  if (json === null || json === undefined || typeof json !== 'object' || Array.isArray(json)) return DEFAULT_SNAP;
  const o = json as Readonly<Record<string, Json>>;
  const bool = (k: keyof SnapPrefs, d: boolean): boolean => (typeof o[k] === 'boolean' ? o[k] : d);
  const size =
    typeof o.gridSize === 'number' && o.gridSize > 0 && o.gridSize <= 1350 ? o.gridSize : DEFAULT_SNAP.gridSize;
  return {
    grid: bool('grid', DEFAULT_SNAP.grid),
    gridSize: size,
    centre: bool('centre', DEFAULT_SNAP.centre),
    edges: bool('edges', DEFAULT_SNAP.edges),
    anchors: bool('anchors', DEFAULT_SNAP.anchors),
  };
}

export function snapPrefsJson(p: SnapPrefs): Json {
  return { grid: p.grid, gridSize: p.gridSize, centre: p.centre, edges: p.edges, anchors: p.anchors };
}

export type TestPlayMode = 'autoplay' | 'judged';

export function testPlayModeOf(json: Json | undefined): TestPlayMode {
  return json === 'judged' ? 'judged' : 'autoplay';
}

/** Beats before the playhead test play starts from (DESIGN.md §4.9: 2). */
export function leadInBeatsOf(json: Json | undefined): number {
  return typeof json === 'number' && Number.isFinite(json) && json >= 0 && json <= 64 ? json : 2;
}

/** Lane linking is on unless turned off. */
export function laneLinkOf(json: Json | undefined): boolean {
  return json !== false;
}

/** The readout under the preview (Shift+I) is shown unless turned off. */
export function readoutOf(json: Json | undefined): boolean {
  return json !== false;
}

/** Reads an ext preference through a parser. */
export function readExt<T>(settings: SettingsService, key: string, parse: (j: Json | undefined) => T): T {
  return parse(settings.getExt(key));
}

/** Background dim override (0..1) or null. */
export function bgDimOf(json: Json | undefined): number | null {
  return typeof json === 'number' && json >= 0 && json <= 1 ? json : null;
}

/** "16:9", "4:3"... for an aspect ratio, else the ratio with two decimals. */
export function aspectName(ar: number): string {
  const known: ReadonlyArray<readonly [number, string]> = [
    [16 / 9, '16:9'],
    [4 / 3, '4:3'],
    [3 / 2, '3:2'],
    [19.5 / 9, '19.5:9'],
    [21 / 9, '21:9'],
    [16 / 10, '16:10'],
    [1, '1:1'],
  ];
  for (const [v, name] of known) if (Math.abs(v - ar) < 1e-3) return name;
  return `${ar.toFixed(2)}:1`;
}
